import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  getProject,
  shareProject,
  updateProjectMember,
  removeProjectMember,
} from "../../services/ChatHistoryService";

const ROLE_LABEL = { owner: "Chủ sở hữu", editor: "Chỉnh sửa", viewer: "Chỉ xem" };

/**
 * Modal chia sẻ dự án — chỉ owner dùng.
 *
 * Props:
 *   open       : boolean
 *   project    : { _id, name }
 *   onClose    : () => void
 */
function ShareProjectModal({ open, project, onClose }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const inputRef = useRef(null);

  const projectId = project?._id;

  const loadDetail = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getProject(projectId);
      setMembers(res?.data?.members || []);
    } catch (e) {
      console.error("getProject:", e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("viewer");
      loadDetail();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, loadDetail]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleShare = async () => {
    const e = email.trim();
    if (!e) {
      toast.error("Nhập email người cần chia sẻ");
      return;
    }
    setSharing(true);
    try {
      await shareProject(projectId, { email: e, role });
      setEmail("");
      await loadDetail();
      toast.success("Đã chia sẻ dự án");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không chia sẻ được");
    } finally {
      setSharing(false);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    try {
      await updateProjectMember(projectId, member._id, { role: newRole });
      await loadDetail();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không đổi được quyền");
    }
  };

  const handleRemove = async (member) => {
    try {
      await removeProjectMember(projectId, member._id);
      await loadDetail();
      toast.success("Đã xoá thành viên");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không xoá được thành viên");
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4"
      onClick={() => onClose?.()}
      style={{ animation: "fadeIn 0.12s ease-out" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "pimSlideUp 0.15s ease-out" }}
      >
        <div className="p-5">
          <h3 className="text-base font-semibold text-gray-900">
            Chia sẻ dự án
          </h3>
          <p className="mt-1 text-sm text-gray-500 truncate">
            {project?.name}
          </p>
        </div>

        {/* Add member */}
        <div className="px-5 pb-3 flex gap-2 items-stretch">
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleShare()}
            placeholder="email@example.com"
            disabled={sharing}
            className="flex-1 min-w-0 px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={sharing}
            className="px-2.5 py-2.5 text-sm border border-gray-300 rounded-xl outline-none focus:border-red-500 bg-white"
          >
            <option value="viewer">Chỉ xem</option>
            <option value="editor">Chỉnh sửa</option>
          </select>
          <button
            type="button"
            onClick={handleShare}
            disabled={sharing || !email.trim()}
            className="px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
          >
            {sharing ? "Đang..." : "Chia sẻ"}
          </button>
        </div>

        {/* Members list */}
        <div className="px-5 pb-2 max-h-72 overflow-y-auto">
          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Thành viên
          </div>
          {loading ? (
            <p className="py-3 text-sm text-gray-400">Đang tải…</p>
          ) : members.length === 0 ? (
            <p className="py-3 text-sm text-gray-400">Chưa có thành viên.</p>
          ) : (
            <div className="space-y-1">
              {members.map((m) => (
                <div
                  key={m._id}
                  className="flex items-center gap-2 py-2 px-1 rounded-lg hover:bg-gray-50"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                    {(m.username || m.email || "U").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-900 truncate">
                      {m.username || m.email}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {m.email}
                    </div>
                  </div>

                  {m.role === "owner" ? (
                    <span className="text-xs text-gray-500 px-2">
                      {ROLE_LABEL.owner}
                    </span>
                  ) : (
                    <>
                      <select
                        value={m.role}
                        onChange={(e) => handleRoleChange(m, e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-200 rounded-lg outline-none focus:border-red-500 bg-white"
                      >
                        <option value="viewer">Chỉ xem</option>
                        <option value="editor">Chỉnh sửa</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => handleRemove(m)}
                        title="Xoá thành viên"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200/70 rounded-lg transition-colors"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ShareProjectModal;
